// Hook to fetch and cache the current user's context (admin status, etc).
// Called once after authentication; provides data via React context.

import { createContext, useContext } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { queryKeys } from '@/lib/api/queryKeys'

export interface UserMe {
  user_id: string
  email: string
  display_name: string
  is_tenant_admin: boolean
}

const UserMeContext = createContext<UserMe | null>(null)

export const UserMeProvider = UserMeContext.Provider

/** Returns the current user context. Throws if not within provider. */
export function useCurrentUser(): UserMe {
  const ctx = useContext(UserMeContext)
  if (!ctx) {
    throw new Error('useCurrentUser must be used within a UserMeProvider')
  }
  return ctx
}

/** Returns the current user context, or null if still loading. */
export function useCurrentUserOrNull(): UserMe | null {
  return useContext(UserMeContext)
}

/** Hook that fetches /me and returns query result. Used by the shell. */
export function useFetchMe() {
  const api = useApi()
  const { slug } = useTenant()

  return useQuery({
    queryKey: queryKeys.users.me(slug),
    queryFn: () => api.get<UserMe>('/me'),
    staleTime: 5 * 60 * 1000, // 5 minutes — admin status rarely changes
  })
}
